import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Card, CardContent } from '../../components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table';
import { Checkbox } from '../../components/ui/checkbox';
import { toast } from 'sonner';
import { Plus, Pencil, Trash2, Search, RefreshCw } from 'lucide-react';

const Employees = () => {
  const { api, isAdmin } = useAuth();
  const [data, setData] = useState([]);
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [formData, setFormData] = useState({});
  const [selectedRoles, setSelectedRoles] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [employeesRes, rolesRes] = await Promise.all([
        api.get('/employees'),
        api.get('/roles')
      ]);
      setData(employeesRes.data);
      setRoles(rolesRes.data);
    } catch (error) {
      toast.error('Failed to fetch data');
    } finally {
      setLoading(false);
    }
  }, [api]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleOpenDialog = (item = null) => {
    if (item) {
      setEditingItem(item);
      setFormData({
        employee_code: item.employee_code,
        first_name: item.first_name,
        last_name: item.last_name || '',
        email: item.email,
        phone: item.phone || '',
        mobile: item.mobile || '',
        designation: item.designation || '',
        department: item.department || '',
        address: item.address || '',
        city: item.city || '',
        state: item.state || '',
        pincode: item.pincode || '',
        is_active: item.is_active,
      });
      setSelectedRoles((item.roles || []).map(r => r.id) || []);
    } else {
      setEditingItem(null);
      setFormData({
        employee_code: '',
        first_name: '',
        last_name: '',
        email: '',
        password: '',
        phone: '',
        mobile: '',
        designation: '',
        department: '',
        address: '',
        city: '',
        state: '',
        pincode: '',
        is_active: true,
      });
      setSelectedRoles([]);
    }
    setIsDialogOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const submitData = { 
        ...formData, 
        role_ids: selectedRoles 
      };
      
      // Remove empty strings
      Object.keys(submitData).forEach(key => {
        if (submitData[key] === '') submitData[key] = null;
      });

      if (editingItem) {
        delete submitData.password; // Don't update password on edit
        await api.put(`/employees/${editingItem.id}`, submitData);
        toast.success('Employee updated successfully');
      } else {
        if (!submitData.password) {
          toast.error('Password is required for new employee');
          setIsSubmitting(false);
          return;
        }
        await api.post('/employees', submitData);
        toast.success('Employee created successfully');
      }
      setIsDialogOpen(false);
      fetchData();
    } catch (error) {
      toast.error((error.response && error.response.data && error.response.data.detail) || 'Operation failed');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDelete = async (item) => {
    if (!window.confirm('Are you sure you want to delete this employee?')) return;
    try {
      await api.delete(`/employees/${item.id}`);
      toast.success('Employee deleted successfully');
      fetchData();
    } catch (error) {
      toast.error((error.response && error.response.data && error.response.data.detail) || 'Delete failed');
    }
  };

  const toggleRole = (roleId) => {
    setSelectedRoles(prev => 
      prev.includes(roleId) 
        ? prev.filter(id => id !== roleId)
        : [...prev, roleId]
    );
  };

  const filteredData = data.filter(item => {
    if (!searchTerm) return true;
    const term = searchTerm.toLowerCase();
    return (
      (item.employee_code || '').toLowerCase().includes(term) ||
      (item.first_name || '').toLowerCase().includes(term) ||
      (item.last_name || '').toLowerCase().includes(term) ||
      (item.email || '').toLowerCase().includes(term)
    );
  });

  return (
    <div className="p-6 max-w-[1600px] mx-auto animate-fade-in" data-testid="master-employees">
      {/* Header */}
      <div className="page-header">
        <div>
          <h1 className="page-title">Employees</h1>
          <p className="text-slate-500 text-sm">{data.length} records</p>
        </div>
        {isAdmin() && (
          <Button
            onClick={() => handleOpenDialog()}
            className="bg-slate-900 hover:bg-slate-800 rounded-sm"
            data-testid="add-new-btn"
          >
            <Plus className="w-4 h-4 mr-2" />
            Add New
          </Button>
        )}
      </div>

      {/* Search */}
      <Card className="mb-4 border-slate-200">
        <CardContent className="p-4">
          <div className="flex items-center gap-4">
            <div className="relative flex-1 max-w-sm">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                placeholder="Search employees..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 rounded-sm"
                data-testid="search-input"
              />
            </div>
            <Button variant="outline" size="icon" onClick={fetchData} className="rounded-sm">
              <RefreshCw className="w-4 h-4" />
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Data Table */}
      <Card className="border-slate-200">
        <CardContent className="p-0">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="spinner" />
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="bg-slate-50">
                    <TableHead>Code</TableHead>
                    <TableHead>Name</TableHead>
                    <TableHead>Email</TableHead>
                    <TableHead>Designation</TableHead>
                    <TableHead>Department</TableHead>
                    <TableHead>Roles</TableHead>
                    <TableHead>Status</TableHead>
                    {isAdmin() && <TableHead className="text-right">Actions</TableHead>}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredData.map((item) => (
                    <TableRow key={item.id}>
                      <TableCell className="font-mono text-sm">{item.employee_code}</TableCell>
                      <TableCell>{item.first_name} {item.last_name}</TableCell>
                      <TableCell>{item.email}</TableCell>
                      <TableCell>{item.designation || '-'}</TableCell>
                      <TableCell>{item.department || '-'}</TableCell>
                      <TableCell>
                        <div className="flex flex-wrap gap-1">
                          {(item.roles || []).map(role => (
                            <span key={role.id} className="badge-info">{role.code}</span>
                          ))}
                        </div>
                      </TableCell>
                      <TableCell>
                        {item.is_active ? (
                          <span className="badge-success">Active</span>
                        ) : (
                          <span className="badge-danger">Inactive</span>
                        )}
                      </TableCell>
                      {isAdmin() && (
                        <TableCell className="text-right">
                          <div className="flex items-center justify-end gap-1">
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => handleOpenDialog(item)}
                              className="h-8 w-8"
                            >
                              <Pencil className="w-4 h-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => handleDelete(item)}
                              className="h-8 w-8 text-red-600"
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </TableCell>
                      )}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Add/Edit Dialog */}
      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingItem ? 'Edit Employee' : 'Add New Employee'}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit}>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 py-4">
              <div>
                <Label className="form-label">Employee Code <span className="text-red-500">*</span></Label>
                <Input
                  value={formData.employee_code || ''}
                  onChange={(e) => setFormData(prev => ({ ...prev, employee_code: e.target.value }))}
                  placeholder="EMP001"
                  required
                  disabled={!!editingItem}
                  className="rounded-sm"
                />
              </div>
              <div>
                <Label className="form-label">Email <span className="text-red-500">*</span></Label>
                <Input
                  type="email"
                  value={formData.email || ''}
                  onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
                  placeholder="email@company.com"
                  required
                  className="rounded-sm"
                />
              </div>
              <div>
                <Label className="form-label">First Name <span className="text-red-500">*</span></Label>
                <Input
                  value={formData.first_name || ''}
                  onChange={(e) => setFormData(prev => ({ ...prev, first_name: e.target.value }))}
                  placeholder="First Name"
                  required
                  className="rounded-sm"
                />
              </div>
              <div>
                <Label className="form-label">Last Name</Label>
                <Input
                  value={formData.last_name || ''}
                  onChange={(e) => setFormData(prev => ({ ...prev, last_name: e.target.value }))}
                  placeholder="Last Name"
                  className="rounded-sm"
                />
              </div>
              {!editingItem && (
                <div>
                  <Label className="form-label">Password <span className="text-red-500">*</span></Label>
                  <Input
                    type="password"
                    value={formData.password || ''}
                    onChange={(e) => setFormData(prev => ({ ...prev, password: e.target.value }))}
                    placeholder="Password"
                    required={!editingItem}
                    className="rounded-sm"
                  />
                </div>
              )}
              <div>
                <Label className="form-label">Phone</Label>
                <Input
                  value={formData.phone || ''}
                  onChange={(e) => setFormData(prev => ({ ...prev, phone: e.target.value }))}
                  placeholder="Phone Number"
                  className="rounded-sm"
                />
              </div>
              <div>
                <Label className="form-label">Mobile</Label>
                <Input
                  value={formData.mobile || ''}
                  onChange={(e) => setFormData(prev => ({ ...prev, mobile: e.target.value }))}
                  placeholder="Mobile Number"
                  className="rounded-sm"
                />
              </div>
              <div>
                <Label className="form-label">Designation</Label>
                <Input
                  value={formData.designation || ''}
                  onChange={(e) => setFormData(prev => ({ ...prev, designation: e.target.value }))}
                  placeholder="Designation"
                  className="rounded-sm"
                />
              </div>
              <div>
                <Label className="form-label">Department</Label>
                <Input
                  value={formData.department || ''}
                  onChange={(e) => setFormData(prev => ({ ...prev, department: e.target.value }))}
                  placeholder="Department"
                  className="rounded-sm"
                />
              </div>
              <div className="md:col-span-2">
                <Label className="form-label">Address</Label>
                <textarea
                  value={formData.address || ''}
                  onChange={(e) => setFormData(prev => ({ ...prev, address: e.target.value }))}
                  placeholder="Address"
                  className="form-input min-h-[60px]"
                />
              </div>
              <div>
                <Label className="form-label">City</Label>
                <Input
                  value={formData.city || ''}
                  onChange={(e) => setFormData(prev => ({ ...prev, city: e.target.value }))}
                  placeholder="City"
                  className="rounded-sm"
                />
              </div>
              <div>
                <Label className="form-label">State</Label>
                <Input
                  value={formData.state || ''}
                  onChange={(e) => setFormData(prev => ({ ...prev, state: e.target.value }))}
                  placeholder="State"
                  className="rounded-sm"
                />
              </div>
              <div>
                <Label className="form-label">Pincode</Label>
                <Input
                  value={formData.pincode || ''}
                  onChange={(e) => setFormData(prev => ({ ...prev, pincode: e.target.value }))}
                  placeholder="Pincode"
                  className="rounded-sm"
                />
              </div>
              <div>
                <Label className="form-label">Status</Label>
                <Select
                  value={(formData.is_active ? formData.is_active.toString() : 'true')}
                  onValueChange={(val) => setFormData(prev => ({ ...prev, is_active: val === 'true' }))}
                >
                  <SelectTrigger className="rounded-sm">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="true">Active</SelectItem>
                    <SelectItem value="false">Inactive</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="md:col-span-2">
                <Label className="form-label">Assign Roles</Label>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-3 p-3 border rounded-sm bg-slate-50">
                  {roles.map(role => (
                    <div key={role.id} className="flex items-center gap-2">
                      <Checkbox
                        id={`role-${role.id}`}
                        checked={selectedRoles.includes(role.id)}
                        onCheckedChange={() => toggleRole(role.id)}
                      />
                      <label 
                        htmlFor={`role-${role.id}`}
                        className="text-sm font-medium cursor-pointer"
                      >
                        {role.name}
                      </label>
                    </div>
                  ))}
                </div>
              </div>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setIsDialogOpen(false)} className="rounded-sm">
                Cancel
              </Button>
              <Button type="submit" disabled={isSubmitting} className="bg-slate-900 hover:bg-slate-800 rounded-sm">
                {isSubmitting ? 'Saving...' : editingItem ? 'Update' : 'Create'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Employees;
